import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardContent,
  Divider,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  PlaylistAdd as PlaylistAddIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useSiteReimportStore } from '../../stores/siteReimportStore';

interface ReimportMissingLabourItemsCreatorProps {
  siteId: number;
  missingItems: string[];
}

const ReimportMissingLabourItemsCreator: React.FC<
  ReimportMissingLabourItemsCreatorProps
> = ({ siteId, missingItems }) => {
  const {
    createMissingLabourItem,
    createAllMissingLabourItems,
    creatingLabourItems,
    creationErrors,
    createdItems,
    selectedFile,
  } = useSiteReimportStore();

  const handleCreateItem = async (itemName: string) => {
    await createMissingLabourItem(itemName, {
      refreshData: true,
      siteId,
      selectedFile,
    });
  };

  const handleCreateAll = async () => {
    await createAllMissingLabourItems(siteId, selectedFile);
  };

  if (!missingItems.length) {
    return null;
  }

  const remainingItems = missingItems.filter(item => !createdItems.has(item));
  const hasRemainingItems = remainingItems.length > 0;
  const allItemsCreated =
    missingItems.length > 0 && remainingItems.length === 0;

  return (
    <Card
      elevation={2}
      sx={{
        border: '1px solid',
        borderColor: allItemsCreated ? 'success.light' : 'warning.light',
        bgcolor: allItemsCreated ? 'success.50' : 'warning.50',
      }}
      data-testid="reimport-missing-labour-items"
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon
                color={allItemsCreated ? 'success' : 'warning'}
                fontSize="small"
              />
              <Typography
                variant="h6"
                color={allItemsCreated ? 'success.dark' : 'warning.dark'}
                fontWeight="medium"
              >
                {allItemsCreated
                  ? 'All Labour Definitions Created'
                  : 'Missing Labour Definitions'}
              </Typography>
              {!allItemsCreated && (
                <Chip
                  label={`${remainingItems.length} remaining`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>

            {hasRemainingItems && (
              <Button
                variant="contained"
                size="small"
                startIcon={
                  creatingLabourItems ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlaylistAddIcon />
                  )
                }
                onClick={handleCreateAll}
                disabled={creatingLabourItems}
                color="primary"
                sx={{ minWidth: 120 }}
                data-testid="reimport-create-all-button"
              >
                {creatingLabourItems ? 'Creating...' : 'Create All'}
              </Button>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            {allItemsCreated
              ? 'All missing labour definitions have been successfully created. You can now proceed with the re-import.'
              : 'The following labour item definitions are missing from your system. Create them individually or all at once to proceed.'}
          </Typography>

          {creationErrors.length > 0 && (
            <>
              <Divider />
              <Alert severity="error">
                <Typography variant="subtitle2" gutterBottom>
                  Failed to create some items:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {creationErrors.map((error, index) => (
                    <Typography component="li" key={index} variant="body2">
                      {error}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            </>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Labour Items ({missingItems.length}):
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                mt: 1,
              }}
            >
              {missingItems.map(item => {
                const isCreated = createdItems.has(item);
                const isCurrentlyCreating = creatingLabourItems && !isCreated;

                return (
                  <Chip
                    key={item}
                    label={item}
                    size="medium"
                    variant={isCreated ? 'filled' : 'outlined'}
                    color={isCreated ? 'success' : 'warning'}
                    icon={
                      isCreated ? <CheckIcon fontSize="small" /> : undefined
                    }
                    deleteIcon={
                      !isCreated ? (
                        <IconButton
                          size="small"
                          disabled={creatingLabourItems}
                          sx={{
                            '&:hover': {
                              bgcolor: 'primary.50',
                            },
                          }}
                        >
                          {isCurrentlyCreating ? (
                            <CircularProgress size={14} />
                          ) : (
                            <AddIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : undefined
                    }
                    onDelete={
                      !isCreated ? () => handleCreateItem(item) : undefined
                    }
                    sx={{
                      '& .MuiChip-deleteIcon': {
                        color: isCreated ? 'success.main' : 'primary.main',
                        '&:hover': {
                          color: isCreated ? 'success.dark' : 'primary.dark',
                        },
                      },
                      '& .MuiChip-label': {
                        fontWeight: isCreated ? 'medium' : 'normal',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {allItemsCreated && (
            <>
              <Divider />
              <Alert severity="success" icon={<CheckIcon />}>
                <Typography variant="body2" fontWeight="medium">
                  Perfect! All missing labour definitions have been created
                  successfully.
                </Typography>
                <Typography variant="caption" color="success.dark">
                  The preview data will be automatically refreshed and you can
                  proceed to the next step.
                </Typography>
              </Alert>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ReimportMissingLabourItemsCreator;
